import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {Alert, Autocomplete, Divider, Stack, Switch} from "@mui/material";
import {useEffect, useMemo, useState} from "react";
import {useMutation, useQuery} from "react-query";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {debounce} from "lodash";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import {useTranslation} from "react-i18next";
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {
    AsaConfig,
    AsaCredentials,
    AsaDataProcessingRules
} from "crqlar-react-common/dist/models/integrations/asa/asa.config";
import {
    FullSyncConfig,
    PeriodicSyncConfig,
    PollerConfig
} from "crqlar-react-common/dist/models/integrations/pollerConfig.model";
import {AgeCategory} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import {LoadingButton} from "@mui/lab";
import {MbscCalendarEvent} from "@mobiscroll/react";
import {AsaService} from "crqlar-react-common/dist/services/integrations/asa/asa.service";
import {useAllResourceCategoriesForSector} from "crqlar-react-common/dist/utils/hooks/resources/useAllResourceCategories";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {enqueueSnackbar} from "notistack";
import {isRequiredTextMissing} from "src/utils/requiredField.util";

interface Props {
    asaConfigData: UseEntityData<AsaConfig>
}

const AsaConfigForm = ({asaConfigData}: Props) => {

    const {t} = useTranslation()
    const sectors: Sector[] = useAllSectors()

    const readOnlyUser = useReadOnly()

    const asaConfig = asaConfigData.item
    const nameMissing = isRequiredTextMissing(asaConfigData, asaConfig.name)
    const setAsaConfig = asaConfigData.setItem
    const resourceCategories = useAllResourceCategoriesForSector(asaConfig.dataProcessingRules?.spaSectorId)

    const ageCategories = useAllAgeCategories()

    //region Handlers

    const checkConnection = useMutation(
        ['checkAsaConfig', asaConfig.credentials],
        async (event: MbscCalendarEvent) => {
            let success = false
            try {
                if (asaConfig.credentials) {
                    await AsaService.checkConnection(asaConfig.credentials);
                    success = true
                }
            } catch (error) {
            }

            enqueueSnackbar(
                success ? t('Connection successful') : t('Connection failed'),
                {
                    variant: success ? 'success' : 'error',
                    anchorOrigin: {vertical: 'bottom', horizontal: 'center'},
                }
            )
        }
    )

    const updateField = (field: keyof AsaConfig, value: any) => {
        setAsaConfig({...asaConfig, [field]: value})
    }

    const updateCredentialsField = (field: keyof AsaCredentials, value: any) => {
        setAsaConfig({
            ...asaConfig,
            credentials: {
                ...asaConfig.credentials,
                [field]: value
            }
        })
    }

    const updatePollerConfig = (field: keyof PollerConfig, value: any) => {
        setAsaConfig({
            ...asaConfig,
            pollerConfig: {
                ...asaConfig.pollerConfig,
                [field]: value
            }
        })
    }

    const updateFullSyncConfig = (field: keyof FullSyncConfig, value: any) => {
        updatePollerConfig('fullSync', {
            ...asaConfig.pollerConfig?.fullSync,
            [field]: value
        })
    }

    const updateDailyConfig = (field: keyof PeriodicSyncConfig, value: any) => {
        updatePollerConfig('daySync', {
            ...asaConfig.pollerConfig?.daySync,
            [field]: value
        })
    }

    const updateQuickConfig = (field: keyof PeriodicSyncConfig, value: any) => {
        updatePollerConfig('quickSync', {
            ...asaConfig.pollerConfig?.quickSync,
            [field]: value
        })
    }

    const updateDataProcessingRules = (field: keyof AsaDataProcessingRules, value: any) => {
        updateField('dataProcessingRules', {
            ...asaConfig.dataProcessingRules,
            [field]: value
        })
    }

    const [resourceDebouncedQuery, setResourceDebouncedQuery] = useState('')

    const searchResourcesQuery = useQuery(['allResources', resourceDebouncedQuery], async () => {
        const response = await ResourceService.getAllResources({
            name: resourceDebouncedQuery ? [resourceDebouncedQuery] : undefined,
            limit: {
                offset: 0,
                limit: 50,
                sort: [{field: "firstName", direction: "ASC"}]
            }
        })

        return response.content || []
    })

    const resourceDebouncedSearch = useMemo(() =>
            debounce((query) => {
                setResourceDebouncedQuery(query)
            }, 500),
        []
    );

    useEffect(() => {
        return () => {
            resourceDebouncedSearch.cancel();
        };
    }, [resourceDebouncedSearch])

    //endregion Handlers

    //region UI

    const credentialsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Credentials')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Integration name')}
                    tooltip={t('Internal label for identifying this ASA integration configuration.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={asaConfig.name || ''}
                        error={nameMissing}
                        fullWidth
                        onChange={e => updateField('name', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('URL')}
                    tooltip={t('ASA API endpoint used by this integration.')}
                >
                    <TextField
                        id={'url'}
                        size={'small'}
                        value={asaConfig.credentials?.url || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('url', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Username')}
                    tooltip={t('Username used to authenticate against ASA.')}
                >
                    <TextField
                        id={'username'}
                        size={'small'}
                        value={asaConfig.credentials?.username || ''}
                        fullWidth
                        onChange={e => updateCredentialsField('username', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Password')}
                    tooltip={t('Password used to authenticate against ASA.')}
                >
                    <TextField
                        id={'password'}
                        size={'small'}
                        value={asaConfig.credentials?.password || ''}
                        fullWidth
                        type={'password'}
                        onChange={e => updateCredentialsField('password', e.target.value)}
                    />
                </Property>

                <Stack direction={'row'} justifyContent={'flex-start'}>
                    <LoadingButton
                        disabled={readOnlyUser}
                        loading={checkConnection.isLoading}
                        onClick={checkConnection.mutate}
                        variant={'outlined'}
                    >
                        {t('Test connection')}
                    </LoadingButton>
                </Stack>
            </Stack>
        </Stack>
    )

    const pollerConfig = (
        <AdditionalProperties title={t('Data sync setup')} variant={'outlined'}>

            <Typography textAlign={'start'} sx={{mt: 1}}>
                {t('Full sync')}
            </Typography>

            <Property
                title={t('Reservation days past')}
                tooltip={t('How far back full sync should look for reservations.')}
            >
                <TextField
                    id={'reservation-days-past'}
                    size={'small'}
                    value={asaConfig.pollerConfig?.fullSync?.reservationDaysPast || ''}
                    onChange={(e) => updateFullSyncConfig('reservationDaysPast', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

            <Property
                title={t('Reservation days future')}
                tooltip={t('How far into the future full sync should look for reservations.')}
            >
                <TextField
                    id={'reservation-days-future'}
                    size={'small'}
                    value={asaConfig.pollerConfig?.fullSync?.reservationDaysFuture || ''}
                    onChange={(e) => updateFullSyncConfig('reservationDaysFuture', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

            <Property
                title={t('Stale timeout minutes')}
                tooltip={t('Minutes before a full sync job is treated as stale.')}
            >
                <TextField
                    id={'stale-timeout-minutes'}
                    size={'small'}
                    value={asaConfig.pollerConfig?.fullSync?.staleTimeoutMinutes || ''}
                    onChange={(e) => updateFullSyncConfig('staleTimeoutMinutes', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

            <Typography textAlign={'start'} sx={{mt: 2}}>
                {t('Daily sync')}
            </Typography>

            <Property
                title={t('Enabled')}
                tooltip={t('Turns scheduled daily sync on or off.')}
            >
                <Switch
                    id={'daily-sync-scheduled'}
                    checked={asaConfig.pollerConfig?.daySync?.enabled || false}
                    onChange={(e) => updateDailyConfig('enabled', e.target.checked)}
                />
            </Property>

            <Property
                title={t('Reservation days future')}
                tooltip={t('How far into the future daily sync should look for reservations.')}
            >
                <TextField
                    id={'reservation-days-future-daily'}
                    size={'small'}
                    value={asaConfig.pollerConfig?.daySync?.reservationDaysFuture || ''}
                    onChange={(e) => updateDailyConfig('reservationDaysFuture', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

            <Property
                title={t('Stale timeout minutes')}
                tooltip={t('Minutes before a daily sync job is treated as stale.')}
            >
                <TextField
                    id={'stale-timeout-minutes'}
                    size={'small'}
                    value={asaConfig.pollerConfig?.daySync?.staleTimeoutMinutes || ''}
                    onChange={(e) => updateDailyConfig('staleTimeoutMinutes', parseInt(e.target.value))}
                    fullWidth
                />
            </Property>

            <Typography textAlign={'start'} sx={{mt: 2}}>
                {t('Quick sync')}
            </Typography>

            <Property
                title={t('Enabled')}
                tooltip={t('Turns scheduled quick sync on or off.')}
            >
                <Switch
                    id={'quick-sync-scheduled'}
                    checked={asaConfig.pollerConfig?.quickSync?.enabled || false}
                    onChange={(e) => updateQuickConfig('enabled', e.target.checked)}
                />
            </Property>

            <Property
                title={t('Stale timeout minutes')}
                tooltip={t('Minutes before a quick sync job is treated as stale.')}
            >
                <TextField
                    id={'quick-stale-timeout-minutes'}
                    size={'small'}
                    value={asaConfig.pollerConfig?.quickSync?.staleTimeoutMinutes || ''}
                    onChange={(e) => updateQuickConfig('staleTimeoutMinutes', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

            <Property
                title={t('Sync period minutes')}
                tooltip={t('Minutes between scheduled quick sync runs.')}
            >
                <TextField
                    id={'quick-sync-period-minutes'}
                    size={'small'}
                    value={asaConfig.pollerConfig?.quickSync?.syncPeriodMinutes || ''}
                    onChange={(e) => updateQuickConfig('syncPeriodMinutes', parseInt(e.target.value))}
                    fullWidth
                    type={'number'}
                />
            </Property>

        </AdditionalProperties>
    )

    const dataProcessingRules = (
        <AdditionalProperties title={t('Data processing rules')} variant={'outlined'}>

            <Typography variant={'h6'}>
                {t('Accommodation')}
            </Typography>

            <Property
                title={t('Accommodation sector')}
                tooltip={t('Sector where imported accommodation reservations and resources should be mapped.')}
            >
                <Autocomplete
                    id={'accomodation-sector'}
                    options={sectors}
                    getOptionLabel={(option: any) => option.name || ''}
                    value={sectors.find(s => s.id === asaConfig.dataProcessingRules?.accommodationSectorId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('accommodationSectorId', value?.id)
                    }}
                />
            </Property>

            <Property
                title={t('Adults age category')}
                tooltip={t('Age category used for adult guest counts imported from ASA.')}
            >
                <Autocomplete
                    id={'adults-age-categories'}
                    options={ageCategories}
                    getOptionLabel={(option: AgeCategory) => option.name || ''}
                    value={ageCategories.find(s => s.id === asaConfig.dataProcessingRules?.adultsAgeCategoryId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('adultsAgeCategoryId', value?.id || undefined)
                    }}
                />
            </Property>

            <Property
                title={t('Children age category')}
                tooltip={t('Age category used for child guest counts imported from ASA.')}
            >
                <Autocomplete
                    id={'children-age-categories'}
                    options={ageCategories}
                    getOptionLabel={(option: AgeCategory) => option.name || ''}
                    value={ageCategories.find(s => s.id === asaConfig.dataProcessingRules?.childrenAgeCategoryId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('childrenAgeCategoryId', value?.id || undefined)
                    }}
                />
            </Property>

            <Property
                title={t('Banded children age categories')}
                tooltip={t('Optional: ASA sends one total children number. When categories are selected here, children are split into them by the linked guests\' birthdays; children without a birthday fall back to the children age category above.')}
            >
                <Autocomplete
                    id={'banded-children-age-categories'}
                    multiple
                    options={ageCategories}
                    getOptionLabel={(option: AgeCategory) => option.name || ''}
                    value={ageCategories.filter(s => s.id && (asaConfig.dataProcessingRules?.bandedChildrenAgeCategoryIds || []).includes(s.id))}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('bandedChildrenAgeCategoryIds', value.map(v => v.id).filter(Boolean))
                    }}
                />
            </Property>

            <Property
                title={t('Teenager age category')}
                tooltip={t('Age category used for teenager guest counts imported from ASA.')}
            >
                <Autocomplete
                    id={'teenager-age-categories'}
                    options={ageCategories}
                    getOptionLabel={(option: AgeCategory) => option.name || ''}
                    value={ageCategories.find(s => s.id === asaConfig.dataProcessingRules?.teenagerAgeCategoryId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('teenagerAgeCategoryId', value?.id || undefined)
                    }}
                />
            </Property>

            <Property
                title={t('Infant age category')}
                tooltip={t('Age category used for infant guest counts imported from ASA.')}
            >
                <Autocomplete
                    id={'infant-age-categories'}
                    options={ageCategories}
                    getOptionLabel={(option: AgeCategory) => option.name || ''}
                    value={ageCategories.find(s => s.id === asaConfig.dataProcessingRules?.infantAgeCategoryId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('infantAgeCategoryId', value?.id || undefined)
                    }}
                />
            </Property>

            <Property
                title={t('Auto create resource for a room if not found')}
                tooltip={t('Creates missing room resources automatically when imported ASA data references an unknown room.')}
            >
                <Switch
                    id={'auto-create-resource-if-not-found'}
                    checked={asaConfig.dataProcessingRules?.autoCreateResourceIfNotFound}
                    onChange={(e) => updateDataProcessingRules('autoCreateResourceIfNotFound', e.target.checked)}
                />
            </Property>

            <Property
                title={t('Auto create service a room if not found')}
                tooltip={t('Creates missing services automatically when imported ASA rate-plan data references an unknown service.')}
            >
                <Switch
                    id={'auto-create-service-if-not-found'}
                    checked={asaConfig.dataProcessingRules?.autoCreateServiceForRatePlanIfNotFound}
                    onChange={(e) => updateDataProcessingRules('autoCreateServiceForRatePlanIfNotFound', e.target.checked)}
                />
            </Property>

            <Typography variant={'h6'} mt={2}>
                {t('Spa')}
            </Typography>

            <Property
                title={t('Sync spa data')}
                tooltip={t('Imports and maps ASA spa data in addition to accommodation data.')}
            >
                <Switch
                    id={'sync-spa-data'}
                    checked={asaConfig.dataProcessingRules?.syncSpaData}
                    onChange={(e) => updateDataProcessingRules('syncSpaData', e.target.checked)}
                />
            </Property>

            <Property
                title={t('Spa sector')}
                tooltip={t('Sector where imported ASA spa reservations and services should be mapped.')}
            >
                <Autocomplete
                    id={'spa-sector'}
                    options={sectors}
                    getOptionLabel={(option: any) => option.name || ''}
                    value={sectors.find(s => s.id === asaConfig.dataProcessingRules?.spaSectorId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('spaSectorId', value?.id)
                    }}
                />
            </Property>

            <Property
                title={t('Spa employee category')}
                tooltip={t('Resource category used for imported ASA spa employees.')}
            >
                <Autocomplete
                    id={'staff-category'}
                    options={resourceCategories}
                    getOptionLabel={(option: any) => option.name || ''}
                    value={resourceCategories.find(s => s.id === asaConfig.dataProcessingRules?.spaEmployeeCategoryId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('spaEmployeeCategoryId', value?.id)
                    }}
                />
            </Property>

            <Property
                title={t('Spa place category')}
                tooltip={t('Resource category used for imported ASA spa places or rooms.')}
            >
                <Autocomplete
                    id={'staff-category'}
                    options={resourceCategories}
                    getOptionLabel={(option: any) => option.name || ''}
                    value={resourceCategories.find(s => s.id === asaConfig.dataProcessingRules?.spaPlaceCategoryId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        updateDataProcessingRules('spaPlaceCategoryId', value?.id)
                    }}
                />
            </Property>

        </AdditionalProperties>
    )

    const configurationSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Configuration')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Property ID / Name (optional, multiproperty)')}
                    tooltip={t('Optional property identifier assigned to all data imported through this connector.')}
                >
                    <Stack
                        width={'100%'}
                        gap={1}
                    >
                        <Alert
                            variant={'outlined'}
                            severity={'info'}
                            sx={{py: 0, lineHeight: 1, alignItems: 'center'}}
                        >
                            {t('Put property id or name here. If set, all incoming data via this connector (profiles, reservations...) will have this property id. You will be able to filter data by this property id in the future')}
                        </Alert>

                        <Alert
                            variant={'outlined'}
                            severity={'error'}
                            sx={{py: 0, lineHeight: 1, alignItems: 'center'}}
                        >
                            {t('Do not change this value if you are not sure what you are doing! It may cause data inconsistency. If you want to change it, please first delete all data from this connector')}
                        </Alert>

                        <TextField
                            id={'name'}
                            size={'small'}
                            value={asaConfig.propertyId || ''}
                            onChange={(e) => updateField('propertyId', e.target.value)}
                            fullWidth
                            required
                        />
                    </Stack>
                </Property>

                {pollerConfig}
                {dataProcessingRules}
            </Stack>
        </Stack>
    )

    //endregion UI

    return (
        <Stack
            sx={{
                width: '100%',
                gap: 6,
            }}
        >
            {credentialsSection}
            <Divider/>
            {configurationSection}
        </Stack>
    )

}

export default AsaConfigForm;
